import React, { memo, useMemo, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { Dropdown } from 'primereact/dropdown'
import { InputSwitch } from 'primereact/inputswitch'
import { Link } from 'react-router-dom'
import { Tooltip } from 'primereact/tooltip'

import { SContainer, SLabelText } from '../../styles/Form'
import Text from '../../../ui/Text'
import { InfoIcon } from '../../../lib/icons/info-icon'
import { GoToIcon } from '../../../lib/icons/goto-icon'
import { theme } from '../../../lib/theme'
import { ChargePoint, ExternalServer } from '../../redux/api/web-api'
import { isPartnerDomain, isSupportDomain } from '../../../lib/currentDomain'
import { getExternalServerLink } from '../../utils/chargePointUtils'
import { SEditSaveButton } from '../../components/ComponentTypeLabel'
import isOnline from '../../../lib/isOnline'
import AlertModal from '../../../ui/AlertModal'
import useText from '../../hooks/useText'

export type ChargePointExternalServerWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
    externalServersOptions: ExternalServer[] | undefined
    disableLoading: boolean
    disableCpms: boolean
    disabledToggleCpms: boolean
    enableLoading: boolean
    chargePoint: ChargePoint
    toggleOcppAvailability: (force?: boolean) => void
    updateExternalServer: (option?: number | null) => void
}
export const ChargePointExternalServerWidget = memo(function ChargePointExternalServerWidget({
    className,
    style,
    disableCpms,
    externalServersOptions,
    disabledToggleCpms,
    toggleOcppAvailability,
    updateExternalServer,
    chargePoint,
}: ChargePointExternalServerWidgetProps): React.ReactElement {
    const text = useText()
    const [showWarningDialog, setShowWarningDialog] = useState(false)

    const serverOptions = externalServersOptions
        ? externalServersOptions &&
          externalServersOptions?.map((data) => ({
              label: `${data?.description}`,
              value: data?.id,
          }))
        : []

    const externalServerLink = useMemo(() => {
        if (!chargePoint?.additionalInfo?.externalServerId || !externalServersOptions) return

        return getExternalServerLink(chargePoint?.additionalInfo?.externalServerId, externalServersOptions)
    }, [chargePoint?.additionalInfo?.externalServerId, externalServersOptions])
    return (
        <>
            <div className={cn(className)} style={style}>
                <SContainer>
                    {isSupportDomain && !chargePoint?.isOcpp && <Text code={'lblOcppProxy'}></Text>}
                    {isPartnerDomain && (
                        <>
                            {chargePoint?.isOcpp ? (
                                <span className=" flex mt-3 gap-1 mb-1">
                                    <span className="text-700">{text('lblChargingEfficiencyService')}</span>
                                    <Tooltip content={'lblProxyRelayInfoTextPartner'} className="large-content">
                                        <i
                                            className="custom-target-icon pi pi-info-circle pointer mr-2"
                                            style={{ color: 'var(--text-color-info)' }}
                                        ></i>
                                    </Tooltip>

                                    <span></span>
                                </span>
                            ) : (
                                <Text code="lblOcppProxy" />
                            )}
                        </>
                    )}

                    {isSupportDomain && !chargePoint?.isOcpp ? (
                        <SEditSaveButton>
                            <Tooltip content={'lblProxyRelayInfoText'} className="large-content">
                                <InfoIcon size={20} fill={theme.brandColors.CA_Blue} id="cpms-info-icon" />
                            </Tooltip>
                            <Link to="/externalservers" data-testid="external-server-redirect">
                                <GoToIcon color={theme.brandColors.CA_Blue} size={18} />
                            </Link>
                        </SEditSaveButton>
                    ) : null}
                </SContainer>
                {isSupportDomain && !chargePoint?.isOcpp ? (
                    <SLabelText data-testid="relay-label">
                        <Text code="lblOcppServer"></Text>
                    </SLabelText>
                ) : null}

                <span data-testid="cpms-label">
                    <Dropdown
                        className={isPartnerDomain ? 'w-6 sm:w-10 mt-1' : 'w-10 mt-4 ml-2'}
                        data-testid={
                            chargePoint?.additionalInfo?.externalServerId
                                ? `externalServer-select-${chargePoint?.additionalInfo?.externalServerId}`
                                : 'externalServer-select'
                        }
                        id="externalServerId"
                        options={['', ...serverOptions]}
                        value={chargePoint?.additionalInfo?.externalServerId}
                        disabled={disableCpms}
                        filter
                        onChange={(option) => {
                            updateExternalServer(option.value)
                        }}
                        pt={{
                            select: { 'data-testid': 'externalServer-select-input' },
                        }}
                    />
                </span>
                {isPartnerDomain && (
                    <span className="ml-6">
                        {chargePoint?.ocppEnabled ? text('lblEnabledStatus') : text('lblDisabledStatus')}
                    </span>
                )}
                <InputSwitch
                    style={{ float: 'right' }}
                    checked={chargePoint?.ocppEnabled ?? false}
                    onChange={() => (isOnline(chargePoint) ? toggleOcppAvailability() : setShowWarningDialog(true))}
                    data-testid="externalServer-toggle"
                    className={isPartnerDomain ? 'mt-3' : 'mr-2 mt-5'}
                    disabled={disabledToggleCpms}
                />

                {externalServerLink && (
                    <div data-testid="external-url" className="ml-3 mt-2 text-600 text-sm">
                        <Text
                            code="lblExternalServerUrl"
                            values={{
                                externalServerUrl: externalServerLink,
                            }}
                        />
                    </div>
                )}
            </div>
            {showWarningDialog && (
                <AlertModal
                    data-testid="warning-alert"
                    title={text('lblChargerOfflineWarning')}
                    message={<Text code="lblChargerOfflineWarningProceed" />}
                    okButtonText={text('btnProceed')}
                    showCancel={true}
                    dismiss={() => setShowWarningDialog(false)}
                    close={(result: boolean) => {
                        setShowWarningDialog(false)
                        if (!result) {
                            return
                        }

                        toggleOcppAvailability(true)
                    }}
                    className="unlock-installation-settings-modal"
                />
            )}
        </>
    )
})
